import { useState, useEffect, SyntheticEvent } from "react";
import Badge from "@mui/material/Badge";
import axios from "axios";
import PersonOutline from '@mui/icons-material/PersonOutline';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import { DateField, type DataTableColumn } from "@routes-plan/ui";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import DeletedRecordDetailModal from "./modals/DeletedRecordDetailModal";
import DeletedLoansModal, { DeletedLoanSummary } from "./modals/DeletedLoansModal";
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { companyLocale } from '../../utils/format/currency';
import { ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportSelect, ReportTable, RowAction } from "./components/ReportLayout";

interface DeletedRow {
  id: string;
  clientId: number;
  clientDeleted: boolean;
  clientBackupId: number | null;
  loanCount: number;
  description: string;
  reference: string;
  officeName: string;
  routeName: string;
  deletedByName: string;
  deletedAt: string;
}

interface Office { id: number; name: string; }
interface Route { id: number; name: string; }

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;
  routeId: string;
}

interface RegistrosBorradosProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const formatDateTime = (value?: string): string => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString(companyLocale());
};

const DeletedRecords: React.FC<RegistrosBorradosProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  const t = useT();
  setPageTitle("menu.reports.deleted-records");
  const currentDate = nowOperationalDateString();

  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DeletedRow[]>([]);
  const [queried, setQueried] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBackupId, setDetailBackupId] = useState<number | null>(null);

  const [loansListOpen, setLoansListOpen] = useState(false);
  const [loansListClientName, setLoansListClientName] = useState<string | null>(null);
  const [loansListItems, setLoansListItems] = useState<DeletedLoanSummary[]>([]);
  const [loansByClientId, setLoansByClientId] = useState<Record<number, DeletedLoanSummary[]>>({});

  const openClientDetail = (backupId: number) => {
    setDetailBackupId(backupId);
    setDetailOpen(true);
  };

  const openLoansList = (clientId: number, clientName: string) => {
    setLoansListClientName(clientName);
    setLoansListItems(loansByClientId[clientId] || []);
    setLoansListOpen(true);
  };

  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    routeId: "",
  });

  // Oficinas visibles para el usuario (ya vienen filtradas por permiso desde el backend; superadmin ve todas)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert(t("auditReports.deleted.officesLoadError"), "error");
      }
    })();
  }, [showAlert, t]);

  // Cobros por oficina (también ya filtrados por permiso en el backend)
  useEffect(() => {
    (async () => {
      if (!filter.officeId) {
        setOfficeRoutes([]);
        return;
      }
      try {
        const { data } = await axios.get(`/offices/${filter.officeId}/routes`);
        setOfficeRoutes(Array.isArray(data) ? data : []);
        setFilter((prev) => ({ ...prev, routeId: "" }));
      } catch (e) {
        console.error(e);
        showAlert(t("auditReports.deleted.routesLoadError"), "error");
      }
    })();
  }, [filter.officeId, showAlert, t]);

  const validateForm = (): boolean => {
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(t("auditReports.deleted.startAfterEnd"), "warning");
      return false;
    }
    return true;
  };

  const columns: DataTableColumn<DeletedRow>[] = [
    { id: 'description', header: t('auditReports.client'), value: (r) => r.description, sortable: true },
    { id: 'reference', header: t('common.idNumber'), value: (r) => r.reference, sortable: true, hideBelow: 'sm' },
    { id: 'officeName', header: t('auditReports.deleted.office'), value: (r) => r.officeName, sortable: true, hideBelow: 'md' },
    { id: 'routeName', header: t('summaryReports.shared.route'), value: (r) => r.routeName, sortable: true },
    { id: 'deletedByName', header: t('auditReports.deletedBy'), value: (r) => r.deletedByName, sortable: true, hideBelow: 'sm' },
    { id: 'deletedAt', header: t('auditReports.dateTime'), value: (r) => r.deletedAt, render: (r) => formatDateTime(r.deletedAt), csv: (r) => formatDateTime(r.deletedAt), sortable: true },
  ];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    setQueried(true);
    try {
      const payload = {
        startDate: filter.startDate,
        endDate: filter.endDate,
        routeIds: filter.routeId ? [Number(filter.routeId)] : undefined,
      };

      const { data } = await axios.post("/reports/deleted-records", payload);
      const items = Array.isArray(data?.data) ? data.data : [];

      if (items.length === 0) {
        setRows([]);
        setLoansByClientId({});
        showAlert(t("auditReports.deleted.notFound"), "info");
        return;
      }

      const nextLoansByClientId: Record<number, DeletedLoanSummary[]> = {};

      const mapped: DeletedRow[] = items.map((r: any, i: number) => {
        const clientId = Number(r.clientId);
        nextLoansByClientId[clientId] = Array.isArray(r.loans) ? r.loans : [];

        return {
          id: `client-${clientId}-${i}`,
          clientId,
          clientDeleted: Boolean(r.clientDeleted),
          clientBackupId: r.clientBackupId ?? null,
          loanCount: Array.isArray(r.loans) ? r.loans.length : 0,
          description: r.description ?? "",
          reference: r.reference ?? "",
          officeName: r.officeName ?? "",
          routeName: r.routeName ?? "",
          deletedByName: r.deletedByName ?? "",
          deletedAt: r.deletedAt ?? "",
        };
      });

      setLoansByClientId(nextLoansByClientId);
      setRows(mapped);
      showAlert(t("auditReports.deleted.found", { count: mapped.length }), "success");
    } catch (error) {
      console.error("Error obteniendo registros borrados:", error);
      showAlert(t("auditReports.deleted.loadError"), "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <ReportPage title={t("menu.reports.deleted-records")} description={t("auditReports.deleted.description")}>
      <ReportFilters onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)} submitDisabled={loading}>
        <DateField
          label={t("auditReports.startDate")}
          value={filter.startDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, startDate: v ?? "" }))}
          max={minYmd(filter.endDate, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("auditReports.endDate")}
          value={filter.endDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, endDate: v ?? "" }))}
          min={filter.startDate}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <ReportSelect
          label={t("auditReports.deleted.office")}
          options={offices}
          value={filter.officeId}
          onChange={(id) => setFilter((prev) => ({ ...prev, officeId: id }))}
          emptyOption={t("auditReports.deleted.allOffices")}
        />
        <ReportSelect
          label={t("summaryReports.shared.route")}
          options={officeRoutes}
          value={filter.routeId}
          onChange={(id) => setFilter((prev) => ({ ...prev, routeId: id }))}
          emptyOption={t("auditReports.deleted.allRoutes")}
          disabled={!filter.officeId}
        />
      </ReportFilters>

      <ReportResults>
        <ReportTable<DeletedRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          exportFileName={t("auditReports.deleted.exportFileName")}
          ariaLabel={t("menu.reports.deleted-records")}
          emptyState={<ReportEmpty queried={queried} />}
          rowActions={(row) => (
            <>
              {row.clientDeleted && (
                <RowAction
                  label={t("auditReports.deleted.viewClient")}
                  icon={<PersonOutline fontSize="small" />}
                  onClick={() => openClientDetail(Number(row.clientBackupId))}
                />
              )}
              {row.loanCount > 0 && (
                <RowAction
                  label={t("auditReports.deleted.viewLoans", { count: row.loanCount })}
                  icon={
                    <Badge badgeContent={row.loanCount} color="secondary" max={99}>
                      <PaymentsOutlined fontSize="small" />
                    </Badge>
                  }
                  onClick={() => openLoansList(row.clientId, row.description)}
                />
              )}
            </>
          )}
        />
      </ReportResults>

      <DeletedRecordDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        recordType="client"
        backupId={detailBackupId}
        showAlert={showAlert}
      />

      <DeletedLoansModal
        open={loansListOpen}
        onClose={() => setLoansListOpen(false)}
        clientName={loansListClientName}
        loans={loansListItems}
        showAlert={showAlert}
      />
    </ReportPage>
  );
};

export default DeletedRecords;
